import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, RefreshCw, Save, UserRoundCheck, Users } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_TYPE_OPTIONS = [
  { id: 'consulta_gratuita', label: 'Consultoría estratégica' },
  { id: 'estrategia', label: 'Estrategia' },
  { id: 'analisis_planificacion', label: 'Análisis y planificación' },
  { id: 'asesoria_pagada', label: 'Asesoría pagada' },
];
const HOUR_OPTIONS = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '13:45', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00'];

const buildForm = () => ({
  nombre: '',
  email: '',
  bio: '',
  timezone: 'Europe/Madrid',
  google_calendar_email: '',
  specialties: '',
  available_booking_types: BOOKING_TYPE_OPTIONS.map((item) => item.id),
  available_hours: HOUR_OPTIONS,
  active: true,
});

export const BookingConsultantsManager = ({ authFetch }) => {
  const [consultants, setConsultants] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(buildForm());
  const [saving, setSaving] = useState(false);

  const activeCount = useMemo(() => consultants.filter((item) => item.active).length, [consultants]);

  const loadConsultants = async () => {
    try {
      const res = await authFetch(`${API}/admin/booking-consultants`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudieron cargar los consultores');
      setConsultants(data.consultants || []);
    } catch (error) {
      toast.error(error.message || 'No se pudieron cargar los consultores');
    }
  };

  useEffect(() => {
    loadConsultants();
  }, []);

  const toggleArrayValue = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((item) => item !== value) : [...prev[key], value],
    }));
  };

  const startEdit = (consultant) => {
    setEditingId(consultant.id);
    setForm({
      nombre: consultant.nombre || '',
      email: consultant.email || '',
      bio: consultant.bio || '',
      timezone: consultant.timezone || 'Europe/Madrid',
      google_calendar_email: consultant.google_calendar_email || '',
      specialties: (consultant.specialties || []).join(', '),
      available_booking_types: consultant.available_booking_types || [],
      available_hours: consultant.available_hours || [],
      active: consultant.active !== false,
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(buildForm());
  };

  const saveConsultant = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        specialties: form.specialties.split(',').map((item) => item.trim()).filter(Boolean),
        google_calendar_email: form.google_calendar_email || null,
      };
      const url = editingId ? `${API}/admin/booking-consultants/${editingId}` : `${API}/admin/booking-consultants`;
      const method = editingId ? 'PATCH' : 'POST';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo guardar el consultor');
      toast.success(editingId ? 'Consultor actualizado' : 'Consultor creado');
      resetForm();
      loadConsultants();
    } catch (error) {
      toast.error(error.message || 'No se pudo guardar el consultor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="booking-consultants-manager">
      <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="glass-card p-6" data-testid="booking-consultants-summary-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Consultores humanos</p>
              <h3 className="mt-2 text-2xl font-semibold text-text-heading">Gestión operativa de agenda humana</h3>
              <p className="mt-2 text-sm text-text-body">Aquí puedes cargar nombres reales, horarios, especialidades y dejar preparado el email de Google Calendar para la conexión futura.</p>
            </div>
            <button onClick={loadConsultants} className="btn-secondary text-sm py-2 px-4" data-testid="booking-consultants-refresh-button">
              <RefreshCw className="w-4 h-4" /> Refrescar
            </button>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-3 text-sm">
            <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="booking-consultants-active-count">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Activos</p>
              <p className="mt-2 text-2xl font-bold text-text-heading">{activeCount}</p>
            </div>
            <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="booking-consultants-total-count">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Total</p>
              <p className="mt-2 text-2xl font-bold text-text-heading">{consultants.length}</p>
            </div>
            <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="booking-consultants-calendar-ready-count">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Preparados para calendar</p>
              <p className="mt-2 text-2xl font-bold text-text-heading">{consultants.filter((item) => item.calendar_sync_status === 'prepared').length}</p>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {consultants.map((consultant) => (
              <button key={consultant.id} type="button" onClick={() => startEdit(consultant)} className="w-full rounded-[22px] border border-border-subtle bg-card/40 p-4 text-left transition hover:border-primary/30" data-testid={`booking-consultant-card-${consultant.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-lg font-semibold text-text-heading">{consultant.nombre}</p>
                    <p className="mt-1 text-sm text-text-body">{consultant.email}</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${consultant.active ? 'border border-primary/20 bg-primary/10 text-primary' : 'border border-border-subtle text-text-muted'}`}>{consultant.active ? 'Activo' : 'Inactivo'}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.16em] text-text-muted">
                  {(consultant.specialties || []).map((item) => <span key={item} className="rounded-full border border-border-subtle px-2 py-1">{item}</span>)}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-text-muted">
                  <span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> {consultant.available_hours?.length || 0} slots</span>
                  <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {consultant.available_booking_types?.length || 0} tipos</span>
                  <span className="inline-flex items-center gap-1"><UserRoundCheck className="w-3.5 h-3.5" /> Calendar: {consultant.google_calendar_email ? 'listo para conectar' : 'preparado'}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={saveConsultant} className="glass-card p-6 space-y-5" data-testid="booking-consultant-form">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{editingId ? 'Editar consultor' : 'Nuevo consultor'}</p>
              <h3 className="mt-2 text-xl font-semibold text-text-heading">{editingId ? 'Actualizar perfil y agenda' : 'Añadir consultor humano'}</h3>
            </div>
            {editingId && <button type="button" onClick={resetForm} className="btn-secondary text-sm py-2 px-4" data-testid="booking-consultant-cancel-edit">Cancelar</button>}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm text-text-body mb-2">Nombre *</label>
              <input value={form.nombre} onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))} className="input-kinesthetic" required data-testid="booking-consultant-name-input" />
            </div>
            <div>
              <label className="block text-sm text-text-body mb-2">Email *</label>
              <input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} className="input-kinesthetic" required data-testid="booking-consultant-email-input" />
            </div>
            <div>
              <label className="block text-sm text-text-body mb-2">Timezone</label>
              <input value={form.timezone} onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))} className="input-kinesthetic" data-testid="booking-consultant-timezone-input" />
            </div>
            <div>
              <label className="block text-sm text-text-body mb-2">Google Calendar email</label>
              <input type="email" value={form.google_calendar_email} onChange={(e) => setForm((prev) => ({ ...prev, google_calendar_email: e.target.value }))} className="input-kinesthetic" placeholder="Preparado para conectar más adelante" data-testid="booking-consultant-calendar-email-input" />
            </div>
          </div>

          <div>
            <label className="block text-sm text-text-body mb-2">Bio / tono de sesión</label>
            <textarea value={form.bio} onChange={(e) => setForm((prev) => ({ ...prev, bio: e.target.value }))} rows={3} className="input-kinesthetic resize-none" data-testid="booking-consultant-bio-input" />
          </div>

          <div>
            <label className="block text-sm text-text-body mb-2">Especialidades (separadas por coma)</label>
            <input value={form.specialties} onChange={(e) => setForm((prev) => ({ ...prev, specialties: e.target.value }))} className="input-kinesthetic" placeholder="estrategia, onboarding, growth" data-testid="booking-consultant-specialties-input" />
          </div>

          <div>
            <label className="block text-sm text-text-body mb-2">Tipos de cita</label>
            <div className="flex flex-wrap gap-2" data-testid="booking-consultant-booking-types-group">
              {BOOKING_TYPE_OPTIONS.map((item) => (
                <button key={item.id} type="button" onClick={() => toggleArrayValue('available_booking_types', item.id)} className={`rounded-full px-3 py-2 text-xs uppercase tracking-[0.16em] ${form.available_booking_types.includes(item.id) ? 'border border-primary/20 bg-primary/10 text-primary' : 'border border-border-subtle text-text-muted'}`} data-testid={`booking-consultant-type-${item.id}`}>
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm text-text-body mb-2">Horas disponibles</label>
            <div className="grid grid-cols-3 gap-2 md:grid-cols-6" data-testid="booking-consultant-hours-group">
              {HOUR_OPTIONS.map((hour) => (
                <button key={hour} type="button" onClick={() => toggleArrayValue('available_hours', hour)} className={`rounded-xl px-3 py-2 text-xs font-medium ${form.available_hours.includes(hour) ? 'border border-primary/20 bg-primary/10 text-primary' : 'border border-border-subtle text-text-muted'}`} data-testid={`booking-consultant-hour-${hour.replace(':', '-')}`}>
                  {hour}
                </button>
              ))}
            </div>
          </div>

          <label className="inline-flex items-center gap-3 text-sm text-text-body" data-testid="booking-consultant-active-toggle">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))} />
            Consultor activo para asignación automática
          </label>

          <button type="submit" className="btn-primary" disabled={saving} data-testid="booking-consultant-save-button">
            <Save className="w-4 h-4" /> {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear consultor'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookingConsultantsManager;